// ---------------------------------------------------------------------------
// contextTypes.ts
//
// ContextRef is the navigation-time payload that travels in location.state
// when the user clicks "Ask about this" on any platform item.
//
// It is NOT stored directly in Firestore. On first user submit it is converted
// to a ConversationTurn (role: 'system') via contextRefToSystemTurn() and that
// turn is what gets stored.
//
// ContextRef is also the shape stored inside MentionChipBlock.ref — it carries
// just enough metadata for the chip label and hover preview.
// ---------------------------------------------------------------------------

import type {
    ConversationTurn,
    ConversationBlock,
    EventEmbedBlock,
    AssetEmbedBlock,
    AlertEmbedBlock,
    SignalEmbedBlock,
    NewsEmbedBlock,
} from './aiSearchTypes';

export interface TimeRange {
    from: string; // ISO 8601
    to: string; // ISO 8601
}

interface ContextRefBase {
    type: string;
    label: string;
    capturedAt: string; // ISO 8601
}

export interface EventContextRef extends ContextRefBase {
    type: 'event';
    eventId: string;
    slug?: string;
    timeRange: TimeRange;
    conditionIdFilter?: string[];
    signalTypeFilter?: string[];
    showAlerts: boolean;
}

export interface AssetContextRef extends ContextRefBase {
    type: 'asset';
    ticker: string;
    assetType: string;
    timeRange: TimeRange;
}

/**
 * A snapshot is a system-generated ConversationTurn stored in the platform DB.
 * It is authored by the platform (not by a user), and can be:
 *   - displayed standalone in a popup or feed card
 *   - loaded into a conversation as the opening system turn
 *
 * GET /api/snapshots/{id} returns a ConversationTurn directly — blocks already
 * assembled server-side. No client-side block construction needed.
 *
 * contextRefToSystemTurn handles the async fetch for this type.
 */
export interface SnapshotContextRef extends ContextRefBase {
    type: 'snapshot';
    snapshotId: string;
}

export interface AlertContextRef extends ContextRefBase {
    type: 'alert';
    alertId: string;
    marketQuestion?: string;
    eventRef?: {
        eventId: string;
        slug?: string;
        timeRange: TimeRange;
        showAlerts: boolean;
    };
}

export interface SignalContextRef extends ContextRefBase {
    type: 'signal';
    signalId: string;
    conditionId: string;
    marketQuestion?: string;
    eventRef?: {
        eventId: string;
        slug?: string;
        timeRange: TimeRange;
        showAlerts: boolean;
    };
}

export interface NewsContextRef extends ContextRefBase {
    type: 'news';
    articleId: string;
    title: string;
    source: string;
    publishedAt: string;
}

export interface AssetDataContextRef extends ContextRefBase {
    type: 'asset_data';
    ticker: string;
    priceAtCapture?: number;
}

export type ContextRef =
    | EventContextRef
    | AssetContextRef
    | SnapshotContextRef
    | AlertContextRef
    | SignalContextRef
    | NewsContextRef
    | AssetDataContextRef;

// ---------------------------------------------------------------------------
// contextRefToBlocks
//
// Synchronous mapping from a non-snapshot ContextRef to ConversationBlock[].
// Snapshot has its own async fetch path — it does not go through this.
// ---------------------------------------------------------------------------

export function contextRefToBlocks(
    ref: Exclude<ContextRef, SnapshotContextRef>
): ConversationBlock[] {
    switch (ref.type) {
        case 'event':
            return [
                {
                    type: 'event_embed',
                    event_id: ref.eventId,
                    slug: ref.slug,
                    time_range: [ref.timeRange.from, ref.timeRange.to],
                    highlight_condition_ids: ref.conditionIdFilter,
                    signal_type_filter: ref.signalTypeFilter,
                    show_alerts: ref.showAlerts,
                } satisfies EventEmbedBlock,
            ];

        case 'asset':
            return [
                {
                    type: 'asset_embed',
                    ticker: ref.ticker,
                    asset_type: ref.assetType,
                    time_range: [ref.timeRange.from, ref.timeRange.to],
                } satisfies AssetEmbedBlock,
            ];

        case 'alert':
            return [
                {
                    type: 'alert_embed',
                    alert_id: ref.alertId,
                    label: ref.label,
                    event_ref: ref.eventRef
                        ? {
                              event_id: ref.eventRef.eventId,
                              slug: ref.eventRef.slug,
                              time_range: [
                                  ref.eventRef.timeRange.from,
                                  ref.eventRef.timeRange.to,
                              ],
                              show_alerts: ref.eventRef.showAlerts,
                          }
                        : undefined,
                } satisfies AlertEmbedBlock,
            ];

        case 'signal':
            return [
                {
                    type: 'signal_embed',
                    signal_id: ref.signalId,
                    condition_id: ref.conditionId,
                    label: ref.label,
                    event_ref: ref.eventRef
                        ? {
                              event_id: ref.eventRef.eventId,
                              slug: ref.eventRef.slug,
                              time_range: [
                                  ref.eventRef.timeRange.from,
                                  ref.eventRef.timeRange.to,
                              ],
                          }
                        : undefined,
                } satisfies SignalEmbedBlock,
            ];

        case 'news':
            return [
                {
                    type: 'news_embed',
                    article_id: ref.articleId,
                    title: ref.title,
                    source: ref.source,
                    published_at: ref.publishedAt,
                } satisfies NewsEmbedBlock,
            ];

        case 'asset_data':
            // mention-only — no embed block
            return [];
    }
}

// ---------------------------------------------------------------------------
// contextRefToSystemTurn
//
// Converts a ContextRef into a system ConversationTurn.
//
// - Non-snapshot types: synchronous, pure mapping via contextRefToBlocks.
// - Snapshot: async fetch of GET /api/snapshots/{id} which returns the
//   pre-built ConversationTurn directly from the DB.
//
// Overloads let callers get correct sync/async inference from TypeScript:
//
//   // sync — no await needed
//   const turn = contextRefToSystemTurn(eventRef);
//
//   // async — await required
//   const turn = await contextRefToSystemTurn(snapshotRef);
// ---------------------------------------------------------------------------

export function contextRefToSystemTurn(
    ref: Exclude<ContextRef, SnapshotContextRef>
): ConversationTurn;
export function contextRefToSystemTurn(
    ref: SnapshotContextRef
): Promise<ConversationTurn>;
export function contextRefToSystemTurn(
    ref: ContextRef
): ConversationTurn | Promise<ConversationTurn> {
    if (ref.type === 'snapshot') {
        return fetch(`/api/snapshots/${ref.snapshotId}`).then(res => {
            if (!res.ok)
                throw new Error(`Snapshot fetch failed: ${ref.snapshotId}`);
            return res.json() as Promise<ConversationTurn>;
        });
    }
    return {
        role: 'system',
        blocks: contextRefToBlocks(ref),
    };
}
